import type { QueryClient } from '@tanstack/react-query';
import type {
  RealtimeInvalidationStrategy,
  NotificationRealtimeContext,
  UserRealtimeContext,
} from '@/infrastructure/notifications/realtime/types';
import { translationKeys } from '../translations/queries';
import { workflowKeys } from '../workflow/queries';
import { dashboardKeys } from '../dashboard/queries';

/**
 * Realtime invalidation strategy for the i18n module.
 *
 * When a Novu notification with `module: 'i18n'` arrives via WebSocket,
 * this strategy invalidates all i18n-related React Query caches so that
 * active pages (lists, detail views, dashboard) auto-refresh.
 */
export const i18nRealtimeStrategy: RealtimeInvalidationStrategy = {
  module: 'i18n',

  shouldProcess(ctx: NotificationRealtimeContext, user: UserRealtimeContext): boolean {
    // Skip own actions — the user's mutations already handle invalidation via onSuccess
    if (ctx.triggeredByUserId === user.userUuid) {
      return false;
    }

    // Skip notifications from a different tenant (guard for future tenant-scoped translations)
    if (ctx.tenantId && user.tenantUuid && ctx.tenantId !== user.tenantUuid) {
      return false;
    }

    return true;
  },

  invalidate(_ctx: NotificationRealtimeContext, queryClient: QueryClient): void {
    // Broad invalidation — React Query only refetches queries with active subscribers.
    // keepPreviousData on list queries prevents UI jank during background refetch.
    queryClient.invalidateQueries({ queryKey: translationKeys.all });
    queryClient.invalidateQueries({ queryKey: workflowKeys.all });
    queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
  },
};
